/**
 * Admin auth context. Mirrors localStorage admin tokens into React state.
 * Use `useAdminAuth()` to read; signOut revokes refresh server-side.
 */
import React, { createContext, useContext, useEffect, useState } from "react";

import {
  adminLogout,
  clearAdminAuth,
  getAdminAccessToken,
  getAdminRefreshToken,
  getAdminUser,
} from "./admin-api";

const Ctx = createContext({ admin: null, isAuthenticated: false, signOut: () => {} });

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(() => getAdminUser());
  const [hasToken, setHasToken] = useState(() => Boolean(getAdminAccessToken()));

  useEffect(() => {
    const onChange = () => {
      setAdmin(getAdminUser());
      setHasToken(Boolean(getAdminAccessToken()));
    };
    window.addEventListener("shutterkart-admin-auth-change", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("shutterkart-admin-auth-change", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const signOut = async () => {
    const rt = getAdminRefreshToken();
    try {
      if (rt) await adminLogout(rt);
    } catch {
      /* swallow */
    }
    clearAdminAuth();
  };

  return (
    <Ctx.Provider value={{ admin, isAuthenticated: hasToken && Boolean(admin), signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAdminAuth() {
  return useContext(Ctx);
}
